import { SPLIT_TESTING_KEY, TRACKING_PROPERTIES_KEY, USER_ID_KEY } from '$lib/global/constants';

import { isTrackingAllowed, onConsentGranted } from '$lib/global/consent/consentGate';
import loadAnalyticsWrapper, { updateSegmentTraitsKey } from '$lib/global/events/loadAnalytics';
import getQueryParameter from '$lib/global/helper/get-query-parameter';
import { handleAnonId } from '$lib/global/storage';
import { setTrackingProperty } from '$lib/helper';
import { embedOptions } from '$lib/Stores/stores';
import { clearUserAnswers } from '$lib/utils/userAnswerStorage';

import { nanoid } from 'nanoid/non-secure';
import { get } from 'svelte/store';

import { getNowISO } from './helper';
import { integrations as trackingIntegrations } from './integrations';
import { ensureAnalyticsStub, getTrackingTitles } from './tracking';
import { serializeForPipeline } from './TrackingContext';
import { TrackingSession } from './TrackingSession';
import { V3Strategy, V4Strategy } from './TrackingStrategy';
import { campaignLoad } from './v4';
import psProperties from './v4.ps-properties';
import { updateVisitorProperties } from './v4.visitor-properties';

import { trackPage } from './index';

import type { Field } from '$lib/types/Field';
import type { LayoutPageEntry } from '$lib/types/LayoutData';
import type { Page } from '$lib/types/Page';
import type { PageContext, PipelineContext } from './TrackingContext';
import type { TrackingStrategy } from './TrackingStrategy';

// ── Config type ──────────────────────────────────────────────────────────

export interface TrackingServiceConfig {
    campaignId: string;
    companyId: string;
    workspaceId: string;
    campaignVersionId: string;
    trackingVersion: string | undefined;
    preventTracking: boolean;
    analyticsLink: string;
}

/**
 * A navigation that happened while consent was still pending.
 *
 * Route changes and page views are kept in one ordered queue rather than two lists: replaying
 * all route changes first and all page views afterwards would attribute the entry page's view
 * to whichever page the visitor ended up on.
 */
type PendingNavigation =
    | { type: 'route'; slug: string | undefined; pageData: PageContext }
    | { type: 'page'; pageData: Page };

/**
 * Upper bound on buffered navigations. A page with `automaticRedirect` enabled navigates on a
 * timer, so a funnel could advance indefinitely while the banner sits open. The earliest
 * events are the ones worth keeping — they carry entry-page attribution — so this drops the
 * newest once the cap is hit rather than evicting the oldest.
 */
const MAX_PENDING_NAVIGATIONS = 50;

// ── TrackingService ──────────────────────────────────────────────────────

export class TrackingService {
    // Identity
    private _userId: string;

    // Campaign config (immutable after construction)
    private readonly config: TrackingServiceConfig;

    // Page map from layout
    private readonly pages: LayoutPageEntry[];

    // Tracking titles (funnel-level, computed once from v4 fields)
    private _trackingTitles: Record<string, string> = {};

    // Query parameters (parsed once at init, reused on session regeneration)
    private _utm: Record<string, string> = {};
    private _custom: Record<string, string> = {};
    private _thirdParty: Record<string, string> = {};

    private pageContext: PageContext | null = null;

    // Page view dedup (inlined from TrackingPageView)
    private prevPageId: string | null = null;
    private prevPageName: string | null = null;

    // Bootstrap guard (inlined from TrackingInit)
    private _bootstrapped = false;

    // Activation guard — the consent-gated half of bootstrap (CAP-4147)
    private _activated = false;

    // Navigations that arrived while consent was still pending, replayed in order on
    // activation so a visitor who accepts still produces a complete first session.
    private pendingNavigations: PendingNavigation[] = [];

    // Composites
    private session: TrackingSession;

    // Strategy
    private strategy: TrackingStrategy | null = null;

    // A/B variant selection (null = no variant in funnel)
    private _variant: 'A' | 'B' | null = null;

    constructor(config: TrackingServiceConfig, pages: LayoutPageEntry[]) {
        this.config = config;
        this.pages = pages;

        // CAP-4147: reading or writing the persistent visitor id is itself a storage
        // access under § 25 TDDDG, so identity is resolved lazily by the `userId` getter
        // and only persisted once consent is granted (see resolvePersistentUserId).
        this._userId = '';

        // Build composites
        this.session = new TrackingSession();
    }

    // ── Public API: bootstrap (one-time init, called from layout) ───────

    bootstrap(fields: Field[]): void {
        if (this._bootstrapped) return;
        this._bootstrapped = true;

        this.strategy = this.config.trackingVersion === 'v3' ? new V3Strategy() : new V4Strategy();

        // Parse and store query parameters (once — SvelteKit soft nav preserves search params)
        const { utm, custom, thirdParty, additionalTrackingTitles } = getQueryParameter();
        this._utm = utm;
        this._custom = custom;
        this._thirdParty = thirdParty;

        // Build tracking titles from fields + query param names (v4 only)
        if (this.isV4()) {
            this._trackingTitles = {
                ...getTrackingTitles(fields),
                ...additionalTrackingTitles,
            };
        }

        // Safety net — ensure analytics stub exists (no-ops if already created).
        // In-memory only, so it is safe before consent — and it buffers any early
        // window.analytics.* call instead of letting it hit the network.
        ensureAnalyticsStub();

        // The A/B variant decides which page content renders, so it must be picked
        // before the first paint. Picked in memory here; persisted only on activation.
        this.resolveVariant();

        // CAP-4147: everything below this line touches persistent storage or the network
        // (Segment's analytics.js sets ajs_* cookies the moment it loads), so it is
        // deferred until the visitor consents. If consent is denied it never runs.
        onConsentGranted(() => this.activate());
    }

    // ── Consent-gated activation ────────────────────────────────────────

    /**
     * The half of bootstrap that may only run with consent: persist the visitor id and
     * A/B assignment, load analytics.js, then replay whatever navigation happened while
     * the banner was still open.
     */
    private activate(): void {
        if (this._activated) return;
        this._activated = true;

        this.resolvePersistentUserId();
        this.persistVariant();

        const analytics: any = window.analytics;

        if (!analytics.initialize) {
            void loadAnalyticsWrapper({
                companyId: this.config.companyId,
                integrations: { Perspective: !!trackingIntegrations.perspective },
                preventTracking: this.config.preventTracking,
                analyticsLink: this.config.analyticsLink,
            });
        }

        // Drain before replaying: isTrackingAllowed() is already true here, so the replayed
        // calls take the normal path instead of re-buffering.
        const buffered = this.pendingNavigations;
        this.pendingNavigations = [];

        buffered.forEach((navigation) => {
            if (navigation.type === 'route') {
                this.handleRouteChange(navigation.slug, navigation.pageData);
            } else {
                this.trackPageTransition(navigation.pageData);
            }
        });
    }

    private bufferPendingNavigation(navigation: PendingNavigation): void {
        if (this.pendingNavigations.length >= MAX_PENDING_NAVIGATIONS) return;

        this.pendingNavigations.push(navigation);
    }

    /**
     * Promote the visitor id to localStorage on activation.
     *
     * First read wins. If nothing has observed the id yet, an id from a previous consented
     * visit is preferred so returning visitors keep their identity. But once the ephemeral
     * pre-consent id has been handed out it is kept and persisted, because consumers capture
     * it eagerly — Embed.Controller bakes it into the HTML embed's `globalVariables` at render
     * time, and OtpVerificationModal.Controller destructures it — and swapping it afterwards
     * would leave their copy pointing at a different visitor than our events.
     *
     * This matters more than it looks: `perspective__user__id` is a single global key while
     * the consent decision is scoped to campaign + version, so republishing a funnel shows the
     * banner again to visitors who still carry an id from before.
     */
    private resolvePersistentUserId(): void {
        if (!this._userId) {
            this._userId = window.localStorage.getItem(USER_ID_KEY) || nanoid();
        }

        window.localStorage.setItem(USER_ID_KEY, this._userId);
    }

    private resolveVariant(): void {
        if (!this.pages.some((p) => p.isVariant)) return;

        const stored = isTrackingAllowed()
            ? window.sessionStorage.getItem(SPLIT_TESTING_KEY + this.config.campaignId)
            : null;

        if (stored === 'A' || stored === 'B') {
            this._variant = stored;

            return;
        }

        this._variant = Math.random() < 0.5 ? 'A' : 'B';
    }

    /**
     * Write the selected variant to sessionStorage on activation.
     *
     * The in-memory selection is authoritative and storage follows it, never the reverse.
     * resolveVariant() runs before the first paint, so by the time consent arrives `_variant`
     * has already decided what rendered and what any buffered page view described. Adopting a
     * differing stored value here would swap the visitor's content on the next update — the
     * very mid-session switch this is meant to prevent.
     *
     * A stale entry is reachable because the split key is scoped to campaignId while the
     * consent decision is scoped to campaignId + campaignVersionId, so republishing a funnel
     * resets consent without clearing the variant.
     */
    private persistVariant(): void {
        if (!this._variant) return;

        window.sessionStorage.setItem(SPLIT_TESTING_KEY + this.config.campaignId, this._variant);
    }

    // ── Public API: handleRouteChange (called from layout afterNavigate) ─

    handleRouteChange(slug: string | undefined, pageData: PageContext): void {
        // Skip if slug doesn't match any known page (e.g. /consent-policy)
        if (slug && !this.pages.some((p) => p.slug === slug)) return;

        // Update page context BEFORE session resolution so that createNewSession
        // has the correct page data for initVisitorProperties (ps_start_slug, ps_start_page).
        // In-memory only, and other components read it via the pageSlug/pageId getters,
        // so this stays outside the consent gate.
        this.pageContext = pageData;

        // CAP-4147: session resolution writes sessionStorage and emits campaignLoad.
        // Hold it until consent resolves, then replay from activate().
        if (!isTrackingAllowed()) {
            this.bufferPendingNavigation({ type: 'route', slug, pageData });

            return;
        }

        const isHomePage = this.resolveIsHomePage(slug);
        this.resolveSession(isHomePage);
        this.session.setCurrentPage(slug ?? '', isHomePage);
    }

    // ── Public API: trackPageTransition (called from Page.svelte) ────────

    trackPageTransition(pageData: Page): void {
        // CAP-4147: buffer the page view until consent resolves so accepting still
        // records the entry page, while rejecting drops it entirely.
        if (!isTrackingAllowed()) {
            this.bufferPendingNavigation({ type: 'page', pageData });

            return;
        }

        trackPage({
            pageName: pageData.pageName,
            tag: pageData.tag,
            campaignId: pageData.campaignId,
            pageSlug: pageData.pageSlug,
            isLastPage: pageData.isLastPage,
            pageId: pageData.pageId,
            fromPageId: this.prevPageId,
            fromPageName: this.prevPageName,
        });
        this.prevPageId = pageData.pageId;
        this.prevPageName = pageData.pageName;
    }

    // ── Page map helpers ────────────────────────────────────────────────

    private resolveIsHomePage(slug?: string): boolean {
        if (!slug) return true; // root route = homepage
        const entry = this.pages.find((p) => p.slug === slug);
        return entry?.isHomePage ?? false;
    }

    // ── Session resolution ───────────────────────────────────────────────

    private resolveSession(isHomePage: boolean): void {
        const urlSessionId = new URLSearchParams(window.location.search).get('sessionId');
        const hasUrlSessionId = urlSessionId !== null;

        // Soft nav — session already in memory
        if (this.session.getSessionId()) {
            if (hasUrlSessionId && urlSessionId !== this.session.getSessionId()) {
                this.createNewSession();
                return;
            }

            if (isHomePage && this.session.shouldRegenerateForHomepage()) {
                this.createNewSession();
                return;
            }

            // Keep current session (covers same urlSessionId or no urlSessionId)
            return;
        }

        // Hard load — try URL sessionId first
        if (hasUrlSessionId) {
            const peeked = TrackingSession.peekBySessionId(urlSessionId);

            if (
                !peeked || // No traits to restore OR
                (isHomePage && peeked.hasSeenAnyNonHomepage) // Regenerate for homepage if seen non-homepage
            ) {
                this.createNewSession(); // Fall through to createNewSession
                return;
            }

            this.session.tryRestoreFromSessionId(this.config.campaignId, urlSessionId);
            updateSegmentTraitsKey(this.sessionId);

            if (this.isV4()) {
                this.initVisitorProperties();
            }

            return;
        }

        // Hard load, no URL sessionId — try to restore from sessionStorage
        const stored = TrackingSession.peek(this.config.campaignId);

        if (stored) {
            if (isHomePage && stored.hasSeenAnyNonHomepage) {
                this.createNewSession();
                return;
            }

            // Restore the stored session into this.session
            this.session.tryRestore(this.config.campaignId);
            updateSegmentTraitsKey(this.sessionId);

            // Merge URL params into restored session's visitor properties (v4 only)
            if (this.isV4()) {
                this.initVisitorProperties();
            }

            return;
        }

        // Nothing to restore — first visit
        this.createNewSession();
    }

    private createNewSession(): void {
        this.session = new TrackingSession();
        this.session.create(this.config.campaignId);
        this.resetAnalytics();
        this.clearPersonalizationData();
        this.seedTrackingPropertiesFromUrl();

        // Seed visitor properties for the new session (v4 only).
        if (this.isV4()) {
            this.initVisitorProperties();
            campaignLoad(this.pageContext?.pageSlug || '', this.pageContext?.pageName || '');
        }
    }

    private clearPersonalizationData(): void {
        clearUserAnswers(this.config.campaignVersionId);
        window.sessionStorage.removeItem(`${TRACKING_PROPERTIES_KEY}${this.config.campaignId}`);
    }

    /**
     * Re-seed tracking properties from URL query parameters (ps_* / perspective_* and utm_*)
     * after session creation clears personalization data. This ensures that custom variables
     * passed via GET parameters survive across page navigations within the funnel.
     */
    private seedTrackingPropertiesFromUrl(): void {
        const { custom, utm } = getQueryParameter();

        Object.entries(custom).forEach(([key, value]) => {
            if (typeof value === 'string') {
                setTrackingProperty(key, value, this.config.campaignId);
            }
        });

        Object.entries(utm).forEach(([key, value]) => {
            if (typeof value === 'string') {
                setTrackingProperty(key, value, this.config.campaignId);
            }
        });
    }

    private initVisitorProperties(): void {
        const visitorProperties = this.session.getVisitorProperties();

        updateVisitorProperties({
            [psProperties.firstSeenAt]: visitorProperties[psProperties.firstSeenAt] || getNowISO(),
            [psProperties.startSlug]:
                visitorProperties[psProperties.startSlug] || this.pageContext?.pageSlug || '',
            [psProperties.startPage]:
                visitorProperties[psProperties.startPage] || this.pageContext?.pageName || '',
            ...this._utm,
            ...this._custom,
            ...this._thirdParty,
            [psProperties.isEmbed]: get(embedOptions).isEmbed ?? false,
            [psProperties.timezone]: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    private resetAnalytics(): void {
        if (window.analytics) {
            window.analytics.reset();
            handleAnonId();
            updateSegmentTraitsKey(this.sessionId);
        }
    }

    // ── Getters: identity ────────────────────────────────────────────────

    get sessionId(): string {
        return this.session.getSessionId();
    }

    get userId(): string {
        if (!this._userId) {
            // Before consent this is an ephemeral, unpersisted id so features that need
            // *an* id (embed variables, payment return URLs) keep working without
            // writing an identifier to the visitor's device. resolvePersistentUserId()
            // promotes or replaces it once consent is granted.
            this._userId = isTrackingAllowed()
                ? window.localStorage.getItem(USER_ID_KEY) || nanoid()
                : nanoid();
        }

        return this._userId;
    }

    // ── Getters: campaign config ─────────────────────────────────────────

    get campaignId(): string {
        return this.config.campaignId;
    }

    get companyId(): string {
        return this.config.companyId;
    }

    get workspaceId(): string {
        return this.config.workspaceId;
    }

    get campaignVersionId(): string {
        return this.config.campaignVersionId;
    }

    get trackingVersion(): string | undefined {
        return this.config.trackingVersion;
    }

    // ── Getters: page context ────────────────────────────────────────────

    get pageSlug(): string {
        return this.pageContext?.pageSlug ?? '';
    }

    get pageId(): string {
        return this.pageContext?.pageId ?? '';
    }

    get trackingTitles(): Record<string, string> {
        return this._trackingTitles;
    }

    get variant(): 'A' | 'B' | null {
        return this._variant;
    }

    resolveDisplayData(page: Page): Page {
        if (this._variant === 'B' && page.variantPageData) {
            return {
                ...(page.variantPageData as Page),
                inputMap: page.inputMap,
                pageIdSlugMap: page.pageIdSlugMap,
                context: page.context,
            };
        }
        return page;
    }

    get queryParams(): {
        utm: Record<string, string>;
        custom: Record<string, string>;
        thirdParty: Record<string, string>;
    } {
        return { utm: this._utm, custom: this._custom, thirdParty: this._thirdParty };
    }

    // ── Getters: session / visitor ───────────────────────────────────────

    get visitorProperties(): Partial<Record<string, string>> {
        return this.session.getVisitorProperties();
    }

    // ── Context methods ──────────────────────────────────────────────────

    getEventContext(): Partial<PipelineContext> {
        if (!this.pageContext) {
            return {};
        }
        return serializeForPipeline({
            sessionId: this.sessionId,
            // Must go through the getter — identity is resolved lazily (CAP-4147).
            userId: this.userId,
            config: { ...this.config, trackingTitles: this._trackingTitles },
            page: this.pageContext,
        });
    }

    // ── Version helpers ──────────────────────────────────────────────────

    isV3(): boolean {
        return this.config.trackingVersion === 'v3';
    }

    isV4(): boolean {
        return this.config.trackingVersion === 'v4';
    }

    // ── Composite accessors ──────────────────────────────────────────────

    getSession(): TrackingSession {
        return this.session;
    }

    getStrategy(): TrackingStrategy {
        if (!this.strategy) {
            throw new Error('TrackingStrategy not initialized — call bootstrap() first');
        }
        return this.strategy;
    }
}
